import {type DefaultError, useMutation, useQueryClient} from "@tanstack/react-query";
import {queryHttp} from "~/query/queryHttp";
import {environment} from "~/env";
import {notificationStore} from "~/stores/init";
import {CHAT_CONFIG} from "~/pages/chat-agent/services/ChatConfiguration";
import {type DashboardItem, type DashboardList, myDashboardUrl} from "./dashboardsApi";
import {myDashboardsQueryKey} from "./dashboardsQueries";

type DashboardMutationResponse = {message: string};

export type DeleteDashboardVariables = {dashboardId: number};
export type RenameDashboardVariables = {dashboardId: number; title: string};

function assertApiConfigured() {
    if (!CHAT_CONFIG.API_URL) {
        throw new Error("Chat API URL is not configured");
    }
}

/**
 * Applies `patch` to the cached "Your dashboards" list at once, then refetches it so the server has
 * the last word.
 */
function usePatchMyDashboards(databagId: number) {
    const queryClient = useQueryClient();
    return (patch: (dashboards: DashboardItem[]) => DashboardItem[]) => {
        const queryKey = myDashboardsQueryKey(databagId);
        queryClient.setQueryData<DashboardList>(queryKey, list => list && {...list, dashboards: patch(list.dashboards)});
        void queryClient.invalidateQueries({queryKey});
    };
}

/**
 * `DELETE /superset/dashboards/{dashboard_id}`: removes the dashboard from the user's list only; the
 * Superset dashboard itself is untouched.
 */
export function useDeleteDashboardMutation(databagId: number) {
    const patchMine = usePatchMyDashboards(databagId);
    return useMutation<DashboardMutationResponse, DefaultError, DeleteDashboardVariables>({
        onSuccess: (_, {dashboardId}) => patchMine(ds => ds.filter(d => d.dashgenId !== dashboardId)),
        mutationFn: async ({dashboardId}) => {
            if (environment.isTestChat) {
                return {message: "Test mode: delete dashboard skipped"};
            }
            assertApiConfigured();
            const {data} = await queryHttp.request<DashboardMutationResponse>({
                url: myDashboardUrl(dashboardId),
                method: "DELETE",
            });
            return data;
        },
    });
}

/** `PATCH /superset/dashboards/{dashboard_id}/rename` with `{title}`. */
export function useRenameDashboardMutation(databagId: number) {
    const patchMine = usePatchMyDashboards(databagId);
    return useMutation<DashboardMutationResponse, DefaultError, RenameDashboardVariables>({
        onSuccess: (_, {dashboardId, title}) => patchMine(
            ds => ds.map(d => d.dashgenId === dashboardId ? {...d, title} : d),
        ),
        mutationFn: async ({dashboardId, title}) => {
            if (environment.isTestChat) {
                return {message: "Test mode: rename dashboard skipped"};
            }
            assertApiConfigured();
            const {data} = await queryHttp.request<DashboardMutationResponse>({
                url: `${myDashboardUrl(dashboardId)}/rename`,
                method: "PATCH",
                data: {title},
            });
            return data;
        },
    });
}

/** "Could not delete the dashboard. Please try again." and the like, one toast per dashboard. */
export function notifyDashboardError(action: "delete" | "rename", dashboardId: string) {
    notificationStore.show(`Could not ${action} the dashboard. Please try again.`, {
        severity: "error",
        id: `dsh-${action}-error-${dashboardId}`,
        autoHideDuration: 4000,
    });
}
